import { useEffect } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { TutorialDialog } from '@/_shared/components/TutorialDialog';
import { TutorialKeys } from '@/_shared/constants';
import { FeatureFlag } from '@/_shared/constants/featureFlags';
import { useTutorialCheck } from '@/_shared/hooks';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useFeatureFlags } from '@/_shared/hooks/useFeatureFlag';
import { getUserProfileCall } from '@/core/state/api';
import {
  getEmployerNotCompletedSurveysCall,
  postEmployerByIdSurveyResultCall,
} from '@/surveys/state/api';
import { SurveyNotCompleted } from '../SurveyNotCompleted';
import { SurveyResultsEmployee } from '../SurveyResultsEmployee';

export const HomepageEmployee = () => {
  const t = useTranslations('dashboard.learning');
  const tShared = useTranslations();

  const { openDialog: openDialogEmployeeTutorial } = useDialog('employeeLearningTutorial');

  const { isFeatureEnabled } = useFeatureFlags();
  const isChatBotEnabled = isFeatureEnabled(FeatureFlag.CHATBOT);

  const { hasAlreadySeenTutorial } = useTutorialCheck();

  const { firstName, email } = getUserProfileCall.hook();
  const { userHasSurveysToComplete } = getEmployerNotCompletedSurveysCall.hook();
  const {
    loading: loadingPostEmployerByIdSurveyResult,
    success: successPostEmployerByIdSurveyResult,
    error: errorPostEmployerByIdSurveyResult,
    execute: executePostEmployerByIdSurveyResult,
    hasCompletedSurvey,
  } = postEmployerByIdSurveyResultCall.hook();

  const slidesEmployee = [
    {
      title: t('tutorial.employee.slides.slide1.title'),
      description: t('tutorial.employee.slides.slide1.description'),
      assetName: 'tutorialEmployeeSlide1',
    },
    ...(isChatBotEnabled
      ? [
          {
            title: t('tutorial.employee.slides.slide2.title'),
            description: t('tutorial.employee.slides.slide2.description'),
            assetName: 'tutorialEmployeeSlide2',
          },
        ]
      : []),
    {
      title: t('tutorial.employee.slides.slide3.title'),
      description: t('tutorial.employee.slides.slide3.description'),
      assetName: 'tutorialEmployeeSlide3',
    },
    {
      title: t('tutorial.employee.slides.slide4.title'),
      description: t('tutorial.employee.slides.slide4.description'),
      assetName: 'tutorialEmployeeSlide4',
    },
  ];

  useEffect(() => {
    executePostEmployerByIdSurveyResult({
      username: email,
    });
  }, [email, executePostEmployerByIdSurveyResult]);

  useEffect(() => {
    if (!hasAlreadySeenTutorial(TutorialKeys.Employee)) {
      openDialogEmployeeTutorial();
    }
  }, [hasAlreadySeenTutorial, openDialogEmployeeTutorial]);

  if (loadingPostEmployerByIdSurveyResult) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorPostEmployerByIdSurveyResult) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge iconName='info' type='error' label={message} />;
  }

  if (
    successPostEmployerByIdSurveyResult &&
    !loadingPostEmployerByIdSurveyResult &&
    !errorPostEmployerByIdSurveyResult
  ) {
    return (
      <Box>
        <Box display='flex' flexDirection='row' justifyContent='space-between' alignItems='center'>
          <Box>
            <Typography mb='xxs' variant='h1'>
              {t('title', { firstName })}
            </Typography>
            <Typography mb='s' variant='p3/light'>
              {!hasCompletedSurvey && <>{t('description.hasNeverCompletedSurveys')}</>}
              {userHasSurveysToComplete && hasCompletedSurvey && (
                <>{t('description.hasNotCompletedSurveys')}</>
              )}
              {!userHasSurveysToComplete && hasCompletedSurvey && (
                <>{t('description.hasCompletedSurveys')}</>
              )}
            </Typography>
          </Box>
        </Box>
        {userHasSurveysToComplete && <SurveyNotCompleted />}
        {hasCompletedSurvey && <SurveyResultsEmployee />}
        <TutorialDialog
          translationsKey='dashboard.learning.tutorial.employee'
          dialogKey='employeeLearningTutorial'
          slides={slidesEmployee}
        />
      </Box>
    );
  }

  return null;
};
